import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

const CATEGORIES = [
  "5-a-side football",
  "Pub quiz team",
  "BBQ competition team",
  "Local sports team",
  "Independent club / society",
  "Community group",
  "Something else (write it in)",
];

const UNDERDOGS_WE_LIKE = [
  "Sunday league teams that lose 8\u20111 but celebrate one goal like a World Cup final",
  "BBQ teams cooking exclusively on confidence",
  "Pub quiz groups with an inspiring relationship with 9th place",
  "Bowling teams with one member treating league night like the Super Bowl",
  "Darts pairs held together by belief and one unexpectedly good leg",
  "Golf societies dressed like runaway casino entertainers",
  "Tiny local baseball teams with championship-level team photos",
  "\u201cMost Improved\u201d youth teams with dangerously high morale",
  "Football clubs bravely redefining what defence means",
];

export default function Sponsorship() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    team_name: "",
    contact_name: "",
    email: "",
    category: CATEGORIES[0],
    story: "",
  });

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.story.trim().length < 10) {
      toast.error("Tell us a bit more — at least a sentence or two.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsorship", {
        team_name: form.team_name.trim(),
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        category: form.category,
        story: form.story.trim(),
      });
      setSubmitted(true);
      toast.success("Pitch received. Mush will review it personally.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again?";
      toast.error(typeof detail === "string" ? detail : "Couldn't send pitch.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sponsorship-page" className="bg-[#F9F9F4] text-[#1A3626]">
      <SEO
        title="Underdog Sponsorship"
        path="/sponsorship"
        description="Big fashion houses sponsor superstars. Wet Cabbage sponsors the underdog. Pitch your five-a-side squad, pub quiz team, BBQ team or breakaway club for wildly unnecessary sponsorship."
      />

      {/* MASTHEAD */}
      <section className="bg-[#F9F9F4]" data-testid="sponsorship-masthead">
        <div className="mx-auto max-w-[1200px] px-6 pb-10 pt-20 md:px-10 md:pb-14 md:pt-28">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            Underdog sponsorship — programme
          </div>
          <h1
            className="font-display mt-5 max-w-[14ch] text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl md:text-7xl lg:text-8xl"
            data-testid="sponsorship-headline"
          >
            Nike sponsors the elite.
            <br />
            <span className="italic">We sponsor</span>{" "}
            <span>the underdog.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#1A3626] md:text-xl md:leading-[1.6]">
            Big fashion houses spend their money on world-record-breakers,
            Olympic medallists and people who get out of bed before 5am to run
            12 miles in the rain. Beautiful. Inspirational. Genuinely terrifying.
            <br />
            <br />
            We sponsor the rest of you.
          </p>
        </div>
      </section>

      {/* THE CASE — adidas / nike vs wet cabbage */}
      <section className="bg-[#1A3626] py-16 text-[#F9F9F4] md:py-24" data-testid="sponsorship-case">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
            <div>
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#F9F9F4]/60 md:text-xs">
                Big fashion houses
              </div>
              <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight md:text-4xl">
                Sponsor people<br /> who&rsquo;d win anyway.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#F9F9F4]/85 md:text-lg md:leading-[1.7]">
                A 24-year-old who can run a 4-minute mile.<br />
                A football club worth &pound;1.4 billion.<br />
                A skateboarder with their own private ramp in Malibu.<br />
                A tennis player whose serve has its own Wikipedia entry.
              </p>
            </div>
            <div className="border-t border-[#F9F9F4]/20 pt-10 md:border-l md:border-t-0 md:pl-12 md:pt-0">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
                Wet Cabbage
              </div>
              <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight md:text-4xl">
                Sponsors the people<br /> who probably won&rsquo;t.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-[#F9F9F4]/85 md:text-lg md:leading-[1.7]">
                Sunday-league squads with bold haircuts.<br />
                Pub quiz teams with one suspicious cousin.<br />
                Five-a-side legends with a torn hamstring.<br />
                BBQ teams whose marinade is a state secret.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SPONSOR */}
      <section className="bg-[#F9F9F4] py-16 md:py-24" data-testid="sponsorship-list">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
            <div className="md:col-span-5">
              <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
                Who we sponsor
              </div>
              <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
                The kind of energy
                <br className="hidden md:block" /> we&rsquo;re looking for.
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.7]">
                You don&rsquo;t need to be good. You just need to be glorious in
                a very specific, very personal way. We&rsquo;re looking for:
              </p>
            </div>

            <ul
              className="md:col-span-7"
              data-testid="underdog-list"
            >
              {UNDERDOGS_WE_LIKE.map((line, i) => (
                <li
                  key={i}
                  className="group flex items-start gap-5 border-t border-[#1A3626]/15 py-5 last:border-b last:border-[#1A3626]/15 md:gap-6 md:py-6"
                  data-testid={`underdog-${i + 1}`}
                >
                  <span className="font-display mt-1 inline-block w-10 shrink-0 text-[10px] font-bold uppercase tracking-[0.32em] text-[#93C065] md:text-xs">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-display text-lg font-bold leading-tight tracking-tight text-[#1A3626] transition-colors group-hover:text-[#1A3626] md:text-xl lg:text-2xl">
                    {line}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="border-t border-[#1A3626]/10 bg-[#F9F9F4] py-16 md:py-24" data-testid="sponsorship-offer">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
              What you get
            </div>
            <h2 className="font-display mt-4 text-3xl font-black uppercase leading-[0.95] tracking-tight sm:text-4xl md:text-5xl">
              Wildly unnecessary sponsorship.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#1A3626]/85 md:text-lg md:leading-[1.7]">
              We don&rsquo;t do million-pound boot deals or stadium naming
              rights. We do small, weird, genuine things — at no cost to you.
              Every accepted underdog gets some combination of the following:
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <Bullet
              n="01"
              title="Free Wet Cabbage kit"
              body="Hoodies, tees or caps for the squad. Worn proudly between defeats."
            />
            <Bullet
              n="02"
              title="A featured spot on our channels"
              body="A short write-up on the site, plus a moment on our socials when you do something memorable."
            />
            <Bullet
              n="03"
              title="An entirely unofficial title"
              body="You become Wet Cabbage Certified. It means absolutely nothing legally and everything spiritually."
            />
          </div>
        </div>
      </section>

      {/* PITCH FORM */}
      <section
        id="pitch"
        className="bg-[#EBE7DD] py-20 md:py-28"
        data-testid="sponsorship-form-section"
      >
        <div className="mx-auto max-w-[760px] px-6 md:px-10">
          <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#1A3626]/65 md:text-xs">
            Pitch your underdog
          </div>
          <h2 className="font-display mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl md:text-6xl">
            Tell us who you&rsquo;re backing.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
            One curator and a dog named Mush will read every entry. Honest
            answer wins over impressive answer. No need to oversell — your
            mediocrity is, in fact, the point.
          </p>

          {submitted ? (
            <div
              className="mt-10 border-2 border-[#1A3626] bg-[#F9F9F4] p-8 md:p-10"
              data-testid="sponsorship-success"
            >
              <CheckCircle2 className="h-10 w-10 text-[#93C065]" strokeWidth={2.4} />
              <h3 className="font-display mt-4 text-2xl font-black uppercase tracking-tight md:text-3xl">
                Pitch received.
              </h3>
              <p className="mt-3 text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
                You&rsquo;ll hear from us if we&rsquo;re into your team. Mush is
                very busy but reads everything. In the meantime, please consider
                buying a hoodie.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-6 py-3 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] md:text-sm"
                  data-testid="sponsorship-success-shop"
                >
                  Shop the drop
                  <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({
                      team_name: "",
                      contact_name: "",
                      email: "",
                      category: CATEGORIES[0],
                      story: "",
                    });
                  }}
                  className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] px-6 py-3 text-xs font-black uppercase tracking-[0.32em] text-[#1A3626] transition-all hover:-translate-y-0.5 hover:bg-[#1A3626] hover:text-[#F9F9F4] md:text-sm"
                  data-testid="sponsorship-success-another"
                >
                  Submit another pitch
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 gap-6" data-testid="sponsorship-form">
              <Field label="Team / club / group name" required>
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.team_name}
                  onChange={onChange("team_name")}
                  placeholder="e.g. Cabbage FC"
                  className="form-input"
                  data-testid="sp-input-team-name"
                />
              </Field>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Field label="Your name" required>
                  <input
                    type="text"
                    required
                    maxLength={120}
                    value={form.contact_name}
                    onChange={onChange("contact_name")}
                    placeholder="e.g. Dave"
                    className="form-input"
                    data-testid="sp-input-contact-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="dave@example.com"
                    className="form-input"
                    data-testid="sp-input-email"
                  />
                </Field>
              </div>

              <Field label="What kind of underdog are you?" required>
                <select
                  required
                  value={form.category}
                  onChange={onChange("category")}
                  className="form-input"
                  data-testid="sp-input-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Tell us your story"
                required
                helper="Two or three sentences is plenty. Include your record if it&rsquo;s embarrassing."
              >
                <textarea
                  required
                  minLength={10}
                  maxLength={2000}
                  value={form.story}
                  onChange={onChange("story")}
                  placeholder="We&rsquo;ve lost 14 in a row but Steve scored a worldie last week and we&rsquo;re basically peaking."
                  rows={6}
                  className="form-input"
                  data-testid="sp-input-story"
                />
              </Field>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="font-display inline-flex items-center gap-3 border-2 border-[#1A3626] bg-[#1A3626] px-7 py-4 text-xs font-black uppercase tracking-[0.32em] text-[#F9F9F4] transition-all hover:-translate-y-0.5 hover:bg-transparent hover:text-[#1A3626] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 md:px-9 md:py-5 md:text-sm md:tracking-[0.36em]"
                  data-testid="sp-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                      Sending&hellip;
                    </>
                  ) : (
                    <>
                      Submit pitch
                      <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                    </>
                  )}
                </button>
                <span className="text-xs text-[#1A3626]/65 md:text-sm">
                  No bots, just curators and one supervisor dog.
                </span>
              </div>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .form-input{
          width: 100%;
          background: #F9F9F4;
          border: 2px solid #1A3626;
          padding: 14px 16px;
          font-size: 16px;
          color: #1A3626;
          font-family: inherit;
          outline: none;
          transition: box-shadow 150ms ease, transform 150ms ease;
        }
        .form-input:focus{
          box-shadow: 4px 4px 0 0 #93C065;
          transform: translate(-1px, -1px);
        }
        textarea.form-input{
          resize: vertical;
          min-height: 140px;
          line-height: 1.55;
        }
      `}</style>
    </div>
  );
}

function Field({ label, helper, required, children }) {
  return (
    <label className="block">
      <span className="font-display block text-[10px] font-bold uppercase tracking-[0.32em] text-[#1A3626] md:text-xs">
        {label} {required && <span className="text-[#93C065]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
      {helper && (
        <span
          className="mt-2 block text-xs leading-relaxed text-[#1A3626]/65"
          dangerouslySetInnerHTML={{ __html: helper }}
        />
      )}
    </label>
  );
}

function Bullet({ n, title, body }) {
  return (
    <div className="border-t border-[#1A3626]/20 pt-6">
      <div className="font-display text-[10px] font-bold uppercase tracking-[0.4em] text-[#93C065] md:text-xs">
        {n}
      </div>
      <h3 className="font-display mt-3 text-xl font-black uppercase tracking-tight md:text-2xl">
        {title}
      </h3>
      <p className="mt-3 text-base leading-relaxed text-[#1A3626]/85 md:text-lg">
        {body}
      </p>
    </div>
  );
}
